import React, { useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Dispatch } from '@reduxjs/toolkit';

import { noOp } from '@neslotech/utils';

import { AppState } from '../../../../types/state.interface';
import { Filter } from '../../../../types/filter.interface';
import { Client } from '../../../../interfaces/client/client.interface';
import { Category } from '../../../../interfaces/config/category.interface';
import { Sector } from '../../../../interfaces/config/sector.interface';
import { ClientType } from '../../../../interfaces/config/client-type.interface';

import { LoadClientsAction } from '../../../../actions/client/client.types';
import { ClientActions } from '../../../../actions/client/client.actions';
import { CategoryActions } from '../../../../actions/category/category.actions';
import { SectorActions } from '../../../../actions/sector/sector.actions';
import { ClientTypeActions } from '../../../../actions/client-type/client-type.actions';

import { useAppDispatch, useAppSelector } from '../../../../hooks';

import { ReactComponent as ChevronRightIcon } from '../../../../icons/chevron-right-icon.svg';

import PageLayout from '../../../../components/layout/page/PageLayout';
import ClientList from '../../../../components/clients/list/ClientList';
import Button from '../../../../components/button/Button';
import { Loader } from '../../../../components/loader/Loader';
import { DropdownMenuItem } from '../../../../components/dropdown/DropdownMenu';
import { RadioGroup } from '../../../../components/radio/RadioGroup';
import FilterLayout from '../../../../components/layout/filter/FilterLayout';
import ExportClientContainer from '../export/ExportClient.container';
import { Modal } from '../../../../components/modal/Modal';
import Select from '../../../../components/select/Select';
import ClientTable from '../../../../components/clients/list/ClientTable';
import {
  getCookieListTableViewStyle,
  setCookieListTableViewStyle
} from '../../../../tools/filter.util';

const ClientsContainer = () => {
  const navigate: NavigateFunction = useNavigate();
  const cookieViewStyle = getCookieListTableViewStyle();
  const [view_style, setViewStyle] = useState<string>(cookieViewStyle ?? 'list_view');

  const dispatch: Dispatch<LoadClientsAction> = useAppDispatch();
  const clients: Client[] = useAppSelector(({ client_store }: AppState) => client_store.clients);
  const categories: Category[] = useAppSelector(
    ({ category_store }: AppState) => category_store.categories
  );
  const sectors: Sector[] = useAppSelector(({ sector_store }: AppState) => sector_store.sectors);
  const clientTypes: ClientType[] = useAppSelector(
    ({ client_type_store }: AppState) => client_type_store.clientTypes
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    dispatch(CategoryActions.loadCategories(noOp));
    dispatch(SectorActions.loadSectors(noOp));
    dispatch(ClientTypeActions.loadClientTypes(noOp));
    dispatch(ClientActions.loadClients(() => setLoading(false)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menuItems = useMemo(() => {
    return (
      onChange: (newFilters: { [key: string]: number | string | boolean }) => void,
      filters: Filter
    ): DropdownMenuItem[] => [
      {
        text: 'Category',
        onClick: noOp,
        suffix: <ChevronRightIcon />,
        expandable: true,
        expandedItems: (
          <RadioGroup
            name="category_id"
            onChange={onChange}
            value={filters?.category_id as string}
            items={categories
              .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1))
              .map((category: Category) => ({
                label: category.name,
                value: category.id
              }))}
          />
        )
      },
      {
        text: 'Sector',
        onClick: noOp,
        suffix: <ChevronRightIcon />,
        expandable: true,
        expandedItems: (
          <RadioGroup
            name="sector_id"
            onChange={onChange}
            value={filters?.sector_id as string}
            items={sectors
              .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1))
              .map((sector: Sector) => ({
                label: sector.name,
                value: sector.id
              }))}
          />
        )
      },
      {
        text: 'Client Type',
        onClick: noOp,
        suffix: <ChevronRightIcon />,
        expandable: true,
        expandedItems: (
          <RadioGroup
            name="client_type_id"
            onChange={onChange}
            value={filters?.client_type_id as string}
            items={clientTypes
              .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1))
              .map((clientType: ClientType) => ({
                label: clientType.name,
                value: clientType.id
              }))}
          />
        )
      }
    ];
  }, [categories, sectors, clientTypes]);

  const handleViewChange = (object: any) => {
    setViewStyle(object.view_style);
    setCookieListTableViewStyle(object.view_style);
  };

  return (
    <PageLayout title="Clients">
      <FilterLayout
        sortable
        menuItems={menuItems}
        actions={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              label="Export"
              onClick={() => {
                setShowExportModal(true);
              }}
            />
            &nbsp;
            <Button label="Add Client" onClick={() => navigate('add')} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '130px',
                marginLeft: '10px',
                marginTop: '15px'
              }}
            >
              <Select
                name="view_style"
                placeholder="Select View"
                value={view_style}
                items={[
                  { label: 'List View', value: 'list_view' },
                  { label: 'Table View', value: 'table_view' }
                ]}
                onChange={handleViewChange}
              />
            </div>
          </div>
        }
      >
        {loading && <Loader filled />}
        {!loading &&
          (view_style === 'list_view' ? (
            <ClientList clients={clients} />
          ) : (
            <ClientTable clients={clients} />
          ))}
      </FilterLayout>

      {showExportModal ? (
        <Modal>
          <ExportClientContainer setShow={setShowExportModal} />
        </Modal>
      ) : null}
    </PageLayout>
  );
};

export default ClientsContainer;
